import { create } from 'zustand';

interface UiState {
  settingsOpen: boolean;
  setSettingsOpen: (v: boolean) => void;
  toggleSettings: () => void;

  tuningOpen: boolean;
  setTuningOpen: (v: boolean) => void;
  toggleTuning: () => void;

  synthOpen: boolean;
  setSynthOpen: (v: boolean) => void;
  toggleSynth: () => void;

  /** Transport drawer — metronome / quantize / record controls. */
  transportOpen: boolean;
  setTransportOpen: (v: boolean) => void;
  toggleTransport: () => void;

  /** Fullscreen tuner view is active. Camera + hand detection pause
   *  while open (mic owns the hardware) and the string layout is
   *  forced to cents spacing with low-pitch-on-top for the duration. */
  tunerOpen: boolean;
  setTunerOpen: (v: boolean) => void;

  /** User has tapped "start" — audio is unlocked, camera is live. */
  started: boolean;
  setStarted: (v: boolean) => void;

  /** How many octaves of the scale pattern the tuning ruler displays. */
  tuningOctaves: 1 | 2;
  setTuningOctaves: (v: 1 | 2) => void;

  /** String index currently flashing because the preview button is
   *  plucking it — read by every component that draws a string
   *  (ScaleCircle dots, StringRulerRow markers, StringsOverlay lines)
   *  so they pulse in lock-step with the audio. */
  flashStringIdx: number | null;
  flashString: (idx: number, ms?: number) => void;

  /** When true, dragging a string updates the underlying scale pattern
   *  (dot on the circle moves). When false, dragging writes a per-string
   *  override — the scale stays, only that string deviates. Orthogonal
   *  to µ-tone (freeCents): the 2×2 combination yields the four player
   *  modes: strict-semi, strict-micro, free-semi, free-micro. */
  strictAscending: boolean;
  setStrictAscending: (v: boolean) => void;

  /** How strings are laid out vertically across the performance stage.
   *   - 'equal':  uniform physical spacing, like a real đàn tranh
   *   - 'cents':  proportional to pitch distance, good for teaching
   *               because the eye literally sees the intervals */
  stringSpacing: 'equal' | 'cents';
  setStringSpacing: (v: 'equal' | 'cents') => void;
  /** When true, string[0] (lowest pitch) sits at the top of the stage;
   *  default (false) puts it at the bottom, matching a real zither. */
  stringOrderLowTop: boolean;
  setStringOrderLowTop: (v: boolean) => void;
}

export const useUiStore = create<UiState>((set) => ({
  settingsOpen: false,
  setSettingsOpen: (settingsOpen) => set({ settingsOpen }),
  toggleSettings: () => set((s) => ({ settingsOpen: !s.settingsOpen })),

  tuningOpen: false,
  setTuningOpen: (tuningOpen) => set({ tuningOpen }),
  toggleTuning: () => set((s) => ({ tuningOpen: !s.tuningOpen })),

  synthOpen: false,
  setSynthOpen: (synthOpen) => set({ synthOpen }),
  toggleSynth: () => set((s) => ({ synthOpen: !s.synthOpen })),

  transportOpen: false,
  setTransportOpen: (transportOpen) => set({ transportOpen }),
  toggleTransport: () => set((s) => ({ transportOpen: !s.transportOpen })),

  tunerOpen: false,
  setTunerOpen: (tunerOpen) => set({ tunerOpen }),

  started: false,
  setStarted: (started) => set({ started }),

  tuningOctaves: 1,
  setTuningOctaves: (tuningOctaves) => set({ tuningOctaves }),

  flashStringIdx: null,
  flashString: (idx, ms = 220) => {
    set({ flashStringIdx: idx });
    setTimeout(() => {
      set((s) => (s.flashStringIdx === idx ? { flashStringIdx: null } : s));
    }, ms);
  },

  strictAscending: true,
  setStrictAscending: (strictAscending) => set({ strictAscending }),

  stringSpacing: 'equal',
  setStringSpacing: (stringSpacing) => set({ stringSpacing }),
  stringOrderLowTop: false,
  setStringOrderLowTop: (stringOrderLowTop) => set({ stringOrderLowTop }),
}));
